import { useTheme } from '@/components/ThemeProvider';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
// Tooltip components are needed for the non-desktop view
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAuthRedux } from '@/hooks/use-auth-redux';
import type { UserProfile } from '@/lib/supabase';
import { LogOut, Menu, Moon, Settings, Sun, X } from 'lucide-react';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navbar = () => {
  const { theme, setTheme } = useTheme();
  const { user, logout } = useAuthRedux();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { name: 'Ai Connect', path: '/ai-connect' },
    { name: 'Ai Scribe', path: '/ai-scribe' },
    { name: 'Ai Metric', path: '/ai-metric' },
    { name: 'Ai Consult', path: '/ai-consult' },
  ];

  const toggleTheme = () => {
    setTheme(theme === 'light' ? 'dark' : 'light');
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const getUserInitials = (user: UserProfile | null) => {
    if (!user) return 'U';
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    if (!fullName) return 'U';
    return fullName
      .split(' ')
      .map((n: string) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getUserDisplayName = (user: UserProfile | null) => {
    if (!user) return 'User';
    const fullName = `${user.first_name || ''} ${user.last_name || ''}`.trim();
    return fullName || 'User';
  };

  return (
    <>
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[url('/images/ai-eye.jpg')] bg-cover bg-center w-full">
        <div className="absolute inset-0 bg-gradient-to-b from-gray-900/20 via-gray-900/10 to-transparent dark:from-gray-900/40 dark:via-gray-900/20 dark:to-transparent backdrop-blur-[1px] w-full"></div>
        <div className="px-6 py-3 relative w-full">
          <div className="flex items-center justify-between rounded-full backdrop-blur-lg bg-white/10 dark:bg-black/10 border border-white/20 dark:border-white/10 shadow-lg px-6 py-3 w-full">
            {/* Left side: Logo */}
            <div className="flex items-center space-x-3">
              <Link to="/" className="flex items-center space-x-3">
                <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-blue-800 rounded-full flex items-center justify-center shadow-lg">
                  <span className="text-lg font-bold text-white">EV</span>
                </div>
                {/* FIX #1: "EyeVantage" text is now hidden by default and only appears on extra-large (xl) screens. */}
                <span className="hidden xl:block text-xl font-medium text-white drop-shadow-lg">
                  EyeVantage
                </span>
              </Link>
            </div>

            {/* Central Navigation (Visible on tablets `md` and up) */}
            <div className="hidden md:flex items-center">
              <div className="flex items-center bg-black/20 dark:bg-white/10 rounded-full p-1 mx-6">
                {navItems.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="relative px-4 py-2 cursor-pointer transition-all duration-300 text-base font-medium text-white/80 hover:text-white">
                    {item.name}
                    {location.pathname === item.path && (
                      <div className="absolute inset-0.5 bg-gradient-to-br from-purple-500 to-blue-900 rounded-full -z-10" />
                    )}
                  </Link>
                ))}
              </div>
            </div>

            {/* Right side - User Section */}
            <div className="flex items-center space-x-4">
              {/* --- DESKTOP-ONLY CONTROLS --- */}
              {/* FIX #2: This entire block is hidden until the `xl` breakpoint (1280px). */}
              <div className="hidden xl:flex items-center space-x-4">
                {/* Theme Toggle */}
                <div className="flex items-center px-3 py-2 rounded-full bg-black/20 dark:bg-white/10">
                  <Sun className="h-4 w-4 text-white/80 mr-1" />
                  <label className="relative inline-flex items-center cursor-pointer mx-1">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={theme === 'dark'}
                      onChange={toggleTheme}
                    />
                    <div className="w-11 h-6 bg-white/20 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all"></div>
                  </label>
                  <Moon className="h-4 w-4 text-white/80 ml-1" />
                </div>
                {/* Settings */}
                <Link to="/settings">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="p-2 rounded-full bg-black/20 dark:bg-white/10 text-white/80 hover:text-white">
                    <Settings className="h-5 w-5" />
                  </Button>
                </Link>
                {/* Profile with Full Name */}
                <div className="flex items-center space-x-3 px-4 py-2 rounded-full bg-black/20 dark:bg-white/10">
                  <Avatar className="w-8 h-8 border-2 border-white/20">
                    <AvatarFallback className="bg-gradient-to-br from-purple-600 to-blue-600 text-white text-sm font-medium">
                      {getUserInitials(user)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-white">
                    <div className="font-medium text-sm drop-shadow">
                      {getUserDisplayName(user)}
                    </div>
                  </div>
                </div>
                {/* Logout */}
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="text-white/80 hover:text-white hover:bg-white/10 px-4 py-2 rounded-full text-sm font-medium bg-black/20 dark:bg-white/10">
                  Log Out
                </Button>
              </div>

              {/* --- NON-DESKTOP CONTROLS (TABLET & MOBILE) --- */}
              {/* FIX #3: This block is visible by default but becomes hidden at the `xl` breakpoint. */}
              <div className="flex xl:hidden items-center space-x-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Avatar className="w-8 h-8 border-2 border-white/20 cursor-pointer">
                        <AvatarFallback className="bg-gradient-to-br from-purple-600 to-blue-600 text-white text-sm font-medium">
                          {getUserInitials(user)}
                        </AvatarFallback>
                      </Avatar>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>{getUserDisplayName(user)}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
                <Button
                  variant="ghost"
                  onClick={logout}
                  className="text-white/80 hover:text-white hover:bg-white/10 px-3 py-1 rounded-full text-xs font-medium bg-black/20 dark:bg-white/10 hidden xl:block md:block">
                  Log Out
                </Button>
              </div>

              {/* Hamburger Menu (Only for small screens, hidden on `md` and up) */}
              <div className="md:hidden">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                  className="text-white/80 hover:text-white hover:bg-white/10 p-2 rounded-full">
                  {mobileMenuOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay (for small screens only) */}
      {mobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm md:hidden"
          onClick={closeMobileMenu}>
          <div className="fixed top-20 left-4 right-4 z-50">
            <div className="bg-black/40 backdrop-blur-xl p-6 rounded-3xl border border-white/20">
              <div className="flex flex-col space-y-6">
                <div className="flex items-center space-x-3 p-4 bg-black/20 rounded-2xl border border-white/20">
                  <Avatar className="h-10 w-10 border-2 border-white/20">
                    <AvatarFallback className="bg-gradient-to-br from-purple-400 to-pink-400 text-white text-sm font-medium">
                      {getUserInitials(user)}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-white">
                    <div className="font-medium">
                      {getUserDisplayName(user)}
                    </div>
                    <div className="text-sm text-white/70">{user?.email}</div>
                  </div>
                </div>
                <div className="space-y-2">
                  {navItems.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={closeMobileMenu}
                      className="block px-6 py-4 rounded-2xl transition-all duration-300 text-center font-medium text-white/80 hover:text-white hover:bg-white/10">
                      {item.name}
                    </Link>
                  ))}
                </div>
                <div className="border-t border-white/10 pt-4 space-y-3">
                  <Button
                    variant="ghost"
                    onClick={toggleTheme}
                    className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 rounded-xl">
                    <Moon className="h-4 w-4 mr-3" /> Switch Theme
                  </Button>
                  <Link to="/settings" onClick={closeMobileMenu}>
                    <Button
                      variant="ghost"
                      className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 rounded-xl">
                      <Settings className="h-4 w-4 mr-3" /> Settings
                    </Button>
                  </Link>
                  <Button
                    variant="ghost"
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                    className="w-full justify-start text-white/80 hover:text-white hover:bg-white/10 rounded-xl">
                    <LogOut className="h-4 w-4 mr-3" /> Logout
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar;
